/** Shared building blocks for the public MVP pages. */
import { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CoverImage } from '@/components/media/Cover';
import { fmtRome } from '@/lib/romeTime';

/**
 * Deterministic multi-line clamp with a real "…" character (browser-independent).
 * `-webkit-line-clamp` does not reliably paint the ellipsis on every browser, so we
 * measure available width via an offscreen node and trim to the last full word.
 */
export function ClampText({ text = '', lines = 2, className = '', testId }) {
	const ref = useRef(null);
	const [display, setDisplay] = useState(text);

	useLayoutEffect(() => {
		const el = ref.current;
		const parent = el?.parentElement;
		if (!el || !parent) return;
		if (!text) { setDisplay(''); return; }

		const compute = () => {
			const width = el.clientWidth;
			if (!width) return;
			const cs = getComputedStyle(el);
			let lineHeight = parseFloat(cs.lineHeight);
			if (Number.isNaN(lineHeight)) lineHeight = parseFloat(cs.fontSize) * 1.4;
			const maxH = lineHeight * lines + 1;

			const meas = document.createElement('div');
			meas.style.cssText =
				`position:absolute;visibility:hidden;pointer-events:none;left:-9999px;top:0;` +
				`width:${width}px;font-size:${cs.fontSize};font-family:${cs.fontFamily};` +
				`font-weight:${cs.fontWeight};line-height:${cs.lineHeight};` +
				`letter-spacing:${cs.letterSpacing};white-space:normal;word-break:normal;`;
			document.body.appendChild(meas);

			meas.textContent = text;
			if (meas.scrollHeight <= maxH) {
				setDisplay(text);
				document.body.removeChild(meas);
				return;
			}
			let lo = 0, hi = text.length, best = 0;
			while (lo <= hi) {
				const mid = (lo + hi) >> 1;
				meas.textContent = text.slice(0, mid).trimEnd() + '…';
				if (meas.scrollHeight <= maxH) { best = mid; lo = mid + 1; }
				else { hi = mid - 1; }
			}
			let out = text.slice(0, best);
			const sp = out.lastIndexOf(' ');
			if (sp > 0) out = out.slice(0, sp);
			setDisplay(out.trimEnd() + '…');
			document.body.removeChild(meas);
		};

		compute();
		const ro = new ResizeObserver(compute);
		ro.observe(parent);
		return () => ro.disconnect();
	}, [text, lines]);

	return <p ref={ref} data-testid={testId} className={className}>{display}</p>;
}

export const fmtDate = (iso) => fmtRome(iso, { day: '2-digit', month: 'short', year: 'numeric' });
export const fmtDateTime = (iso) => fmtRome(iso, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export function Container({ children, className = '' }) {
	return <div className={`mx-auto max-w-6xl px-5 py-10 ${className}`}>{children}</div>;
}

export function PageTitle({ children, sub }) {
	return (
		<div className="mb-8">
			<h1 className="text-3xl font-extrabold sm:text-4xl" style={{ fontFamily: "'Poppins', sans-serif" }}>{children}</h1>
			{sub && <p className="mt-2 text-slate-400">{sub}</p>}
		</div>
	);
}

/** @param {{to:string,title:string,meta?:string,tag?:string,cover?:string,coverIcon?:any,index?:number}} p */
export function ItemCard({ to, title, meta, tag, cover, coverIcon, index = 0, testId, metaLines = 1, titleLines = 1 }) {
	return (
		<motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: index * 0.04 }}>
			<Link to={to} data-testid={testId}
				className="group block overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/50 transition-all hover:-translate-y-1 hover:border-emerald-400/30">
				<CoverImage url={cover} alt={title} icon={coverIcon} className="h-40 w-full transition-transform duration-500 group-hover:scale-105" />
				<div className="p-5">
					{tag && <span className="mb-2 inline-block rounded-full bg-emerald-400/10 px-2.5 py-0.5 text-[11px] font-medium text-emerald-300">{tag}</span>}
					<h3 title={title} className={`${titleLines === 2 ? 'line-clamp-2 min-h-[3.5rem]' : 'line-clamp-1'} text-lg font-bold leading-7 text-slate-100 group-hover:text-emerald-300`}>{title}</h3>
					{meta && (
						metaLines === 2
							? <ClampText text={meta} lines={2} className="mt-1 min-h-[2.5rem] text-sm text-slate-400" />
							: <p className="mt-1 line-clamp-1 text-sm text-slate-400">{meta}</p>
					)}
				</div>
			</Link>
		</motion.div>
	);
}

export function Empty({ children }) {
	return <p className="rounded-xl border border-slate-800 bg-slate-900/30 p-8 text-center text-slate-500">{children}</p>;
}
